import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import ProductImage from '@sprinx/react-com/ProductImage';
import { NoSsr } from '@material-ui/core';
import { useRecoilValue } from 'recoil';
import ChipColored from '@sprinx/react-mui-components/ChipColored';
import { ProductDetailProduct } from '../../../api/products/productDetail';
import { isAuthenticatedState, showPricesState } from '../../../api/appState';
import { activeFavoriteProducts, showStockAvailability } from '../../../config';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailAddToCart from './ProductDetailAddToCart';
import ProductDetailName from './ProductDetailName';
import ProductDetailPrice from './ProductDetailPrice';
import ProductDetailStockInfo from './ProductDetailStockInfo';
import ProductDetailFavoriteToggle from './ProductDetailFavoriteToggle';

export interface ProductDetailVariantRowProps extends StandardDivProps<ProductDetailVariantRowClassKey> {
  product: ProductDetailProduct;
}

export type ProductDetailVariantRowClassKey =
  | 'root'
  | 'name'
  | 'image'
  | 'addToCart'
  | 'price'
  | 'stockInfo'
  // | 'parameters'
  | 'productType'
  | 'favoriteToggle'
  | 'outletChipWrapper';

const themeSettings = { name: 'ProductDetailVariantRow' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ProductDetailVariantRowClassKey, {}>({
      root: {
        display: 'grid',
        gridTemplateColumns: '0.1fr 0.125fr 1fr 1fr 250px',
        gridTemplateAreas: `
          "image favoriteToggle name  name  stockInfo"
          "image favoriteToggle price price addToCart"
        `,

        [theme.breakpoints.down('xs')]: {
          gridTemplateColumns: '130px 1fr',
          // gridTemplateRows: 'auto auto minmax(48px, auto)',
          gridTemplateAreas: `
            "image        image"
            "favoriteToggle        favoriteToggle"
            "name         name"
            "price        price"
            "stockInfo    addToCart"
          `,
          padding: theme.spacing(1, 0),
        },
        [theme.breakpoints.up('md')]: {
          gridTemplateColumns: '0.1fr 0.125fr 1fr 1fr 250px',
          gridTemplateAreas: `
            "image favoriteToggle name   price   stockInfo"
            "image favoriteToggle name   price   addToCart"
          `,
        },
      },
      image: {
        gridArea: 'image',
        display: 'flex',
        'align-items': 'center',
        height: '100%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),

        // Fixes grid in IE 10+
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 1,
          '-ms-grid-column': 1,
          '-ms-grid-row-span': 2,
        },
      },
      favoriteToggle: {
        gridArea: 'favoriteToggle',
        opacity: 1,
      },
      name: {
        gridArea: 'name',
        '& > div > h2': { fontSize: 18 },
      },
      addToCart: {
        gridArea: 'addToCart',
        minHeight: '40px',
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'flex-end',
        },
      },
      price: {
        gridArea: 'price',
        justifyContent: 'space-around',
      },
      stockInfo: {
        gridArea: 'stockInfo',
        alignItems: 'center',
        paddingBottom: 0,
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'flex-start',
        },
      },
      // parameters: {
      //   gridArea: 'parameters',
      //   display: 'flex',
      // },
      productType: {
        marginLeft: theme.spacing(2),
      },
      outletChipWrapper: {
        marginRight: theme.spacing(1),
        '& .MuiChip-label': {
          color: `${theme.palette.common.white} !important`,
          fontWeight: 600,
        },
      },
    }),
  themeSettings,
);

const ProductDetailVariantRow = React.forwardRef<HTMLDivElement, ProductDetailVariantRowProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const isAuthenticated = useRecoilValue(isAuthenticatedState);
    const showPrices = useRecoilValue(showPricesState);
    const t = useTranslateWithFallback();

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <ProductImage
          className={classes.image}
          src={product.image as any}
          alt={t(product.name)}
          size='small'
          type='image'
        />
        {activeFavoriteProducts && isAuthenticated && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggle} productId={product.id} />
          </NoSsr>
        )}
        <div className={classes.name}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {product.taxonomies?.includes('/catalogue/outlet') && (
              <ChipColored label='OUTLET' color='#FFA800' size='small' className={classes.outletChipWrapper} />
            )}
            <ProductDetailName className={classes.name} product={product} />
          </div>
          <span>
            {t('common/code')}: {product.sku || ''}
          </span>
        </div>
        {/* <ProductDetailParameters className={classes.parameters} product={product} /> */}
        {showStockAvailability && isAuthenticated && (
          <ProductDetailStockInfo className={classes.stockInfo} product={product} />
        )}
        {isAuthenticated && showPrices && <ProductDetailPrice className={classes.price} product={product} />}
        {isAuthenticated && <ProductDetailAddToCart className={classes.addToCart} product={product} />}
      </div>
    );
  },
);

ProductDetailVariantRow.displayName = 'ProductDetailVariantRow';

export default withThemeProps(themeSettings)(ProductDetailVariantRow);

// const ProductDetailParameters: React.FC<ProductDetailVariantRowProps> = ({ product, className }) => {
//   const t = useTranslateWithFallback();
//   return (
//     <div className={className}>
//       <TextFieldPreview label={t('common/code')} value={product.sku || ''} size='small' />
//       <NumberFieldPreview
//         label={t('productPage/minOrderableQuantity')}
//         value={product.quantityStep}
//         size='small'
//         decimalPrecision={0}
//       />
//       <TextFieldPreview label={t('common/unit')} value={product.unit} size='small'  />
//     </div>
//   );
// };
